import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR, formatDateTimeBR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Loader2, Layers, FileText, Handshake, CheckCircle2, AlertCircle, Clock, RotateCcw, XCircle, DollarSign, Wallet, Settings2, ChevronDown, Percent, Calendar, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  EditInstallmentInterestModal,
  EditInstallmentDueDatesModal,
  EditInstallmentValuesModal,
} from "../components/EditInstallmentModals";
import RecoveryToggleModal from "../components/RecoveryToggleModal";

const STATUS_BADGE = {
  ativo: { label: "Ativo", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  quitado: { label: "Quitado", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  cancelado: { label: "Cancelado", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" },
};
const INST_STATUS = {
  pendente: { label: "Pendente", cls: "text-amber-300", Icon: Clock },
  paga: { label: "Paga", cls: "text-emerald-300", Icon: CheckCircle2 },
  atrasada: { label: "Atrasada", cls: "text-red-300", Icon: AlertCircle },
  cancelada: { label: "Cancelada", cls: "text-zinc-400", Icon: XCircle },
};

export default function ParceladoDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "gestora";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(null);
  const [editModal, setEditModal] = useState(null); // 'interest' | 'due-dates' | 'values' | null
  const [recoveryModal, setRecoveryModal] = useState(null); // 'enter' | 'return' | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/installment-contracts/${id}`);
      setData(data);
      try {
        const { data: rec } = await api.get(`/recovery/check/installment_contract/${id}`);
        setRecovery(rec);
      } catch (_) { setRecovery(null); }
    } catch (e) {
      toast.error("Falha ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const pay = async (n) => {
    if (!window.confirm(`Confirmar pagamento da parcela ${n}?`)) return;
    try {
      await api.post(`/installment-contracts/${id}/installments/${n}/pay`, {});
      toast.success(`Parcela ${n} paga`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao pagar");
    }
  };

  const revert = async (n) => {
    if (!window.confirm(`Estornar pagamento da parcela ${n}?`)) return;
    try {
      await api.delete(`/installment-contracts/${id}/installments/${n}/pay`);
      toast.success(`Parcela ${n} estornada`);
      load();
    } catch (e) {
      toast.error("Falha ao estornar");
    }
  };

  const cancel = async () => {
    const reason = window.prompt("Motivo do cancelamento:");
    if (!reason) return;
    try {
      await api.post(`/installment-contracts/${id}/cancel`, { reason });
      toast.success("Contrato cancelado");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 text-[#737373] text-sm">
          <Loader2 size={14} className="animate-spin" /> Carregando...
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="fc-card p-10 text-center">
          <div className="text-sm text-[#A3A3A3]">Contrato não encontrado.</div>
          <button onClick={() => nav("/parcelados")} className="fc-btn-ghost mt-4">Voltar</button>
        </div>
      </Layout>
    );
  }

  const sb = STATUS_BADGE[data.status] || STATUS_BADGE.ativo;
  const Icon = data.kind === "parcelado" ? FileText : Handshake;
  const KindCls = data.kind === "parcelado" ? "text-blue-300 border-blue-500/30 bg-blue-500/10" : "text-violet-300 border-violet-500/30 bg-violet-500/10";
  const commission = data.commission;

  return (
    <Layout>
      <button onClick={() => nav("/parcelados")} className="fc-btn-ghost flex items-center gap-1.5 mb-4 text-[12.5px]" data-testid="back-btn">
        <ArrowLeft size={13} /> Voltar
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-1.5 mb-1">
            <Layers size={11} /> Operação alternativa
          </div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`badge-base border ${KindCls}`}><Icon size={10} /> {data.kind.toUpperCase()}</span>
            <span className={`badge-base border ${sb.cls}`}>{sb.label}</span>
            {recovery?.in_recuperacao && (
              <span
                className="badge-base bg-amber-500/15 text-amber-200 border border-amber-500/50 font-medium tracking-wider"
                title={`Em recuperação — ${recovery.dias_atraso} dias de atraso`}
                data-testid="parcelado-badge-recuperacao"
              >
                RECUPERAÇÃO · {recovery.dias_atraso}d
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">
            <Link to={`/clientes/${data.client_id}`} className="hover:text-[#D4AF37] transition-colors">{data.client_name}</Link>
          </h1>
          <p className="text-[13px] text-[#A3A3A3] mt-1">Sócio: {data.socio_name} {data.indicator_name ? `· Indicador: ${data.indicator_name}` : ""}</p>
        </div>
        {data.status === "ativo" && (
          <div className="flex items-center gap-2">
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="fc-btn-ghost flex items-center gap-1.5 text-[12.5px]" data-testid="operational-changes-trigger" title="Central de Alterações Operacionais">
                    <Settings2 size={13} /> Alterações Operacionais <ChevronDown size={11} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                  <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-[#737373]">
                    Editar parcelado/acordo
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {data.kind === "parcelado" && (
                    <DropdownMenuItem onSelect={() => setEditModal("interest")} data-testid="menu-edit-interest" className="cursor-pointer">
                      <Percent size={13} className="mr-2 text-[#D4AF37]" /> Alterar % de juros
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => setEditModal("due-dates")} data-testid="menu-edit-due-dates" className="cursor-pointer">
                    <Calendar size={13} className="mr-2 text-[#D4AF37]" /> Alterar vencimento
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setEditModal("values")} data-testid="menu-edit-values" className="cursor-pointer">
                    <DollarSign size={13} className="mr-2 text-[#D4AF37]" /> Alterar valores
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {data.in_recovery ? (
                    <DropdownMenuItem onSelect={() => setRecoveryModal("return")} data-testid="menu-recovery-return" className="cursor-pointer">
                      <RotateCcw size={13} className="mr-2 text-emerald-300" /> Retornar da Recuperação
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => setRecoveryModal("enter")} data-testid="menu-recovery-enter" className="cursor-pointer">
                      <LifeBuoy size={13} className="mr-2 text-amber-300" /> Enviar para Recuperação
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <button onClick={cancel} className="fc-btn-ghost text-red-300 border-red-500/30" data-testid="cancel-ic-btn">
              <XCircle size={13} /> Cancelar contrato
            </button>
          </div>
        )}
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={formatBRL(data.total_amount)} />
        <Stat label="Capital" value={formatBRL(data.capital)} />
        <Stat label="Lucro" value={formatBRL(data.lucro_total)} accent />
        <Stat label="Saldo restante" value={formatBRL(data.balance_remaining)} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Parcelas */}
        <div className="lg:col-span-2">
          <div className="fc-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-[16px] font-semibold">
                Parcelas <span className="text-[#737373] text-[12px] ml-2 font-normal">
                  {data.paid_count}/{data.installments_count} pagas · {data.frequency === "weekly" ? "semanal" : "mensal"}
                </span>
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="installments-table">
                <thead>
                  <tr className="border-b border-[#1f1f1f] text-[10px] uppercase tracking-wider text-[#737373]">
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Vencimento</th>
                    <th className="text-right py-2">Valor</th>
                    <th className="text-right py-2">Capital</th>
                    <th className="text-right py-2">Lucro</th>
                    <th className="text-left py-2 pl-3">Status</th>
                    <th className="text-right py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.installments.map((i) => {
                    const st = INST_STATUS[i.status] || INST_STATUS.pendente;
                    const IS = st.Icon;
                    return (
                      <tr key={i.id} className="border-b border-[#141414] hover:bg-[#0d0d0d]" data-testid={`installment-row-${i.number}`}>
                        <td className="py-2.5 text-white font-mono">{i.number}/{i.total}</td>
                        <td className="py-2.5">{formatDateBR(i.due_date)}</td>
                        <td className="py-2.5 text-right font-mono">{formatBRL(i.amount)}</td>
                        <td className="py-2.5 text-right text-[#A3A3A3] font-mono">{formatBRL(i.capital_share)}</td>
                        <td className="py-2.5 text-right text-[#A3A3A3] font-mono">{formatBRL(i.lucro_share)}</td>
                        <td className={`py-2.5 pl-3 ${st.cls}`}>
                          <span className="inline-flex items-center gap-1.5"><IS size={11} /> {st.label}</span>
                          {i.paid_at && <div className="text-[10px] text-[#737373] mt-0.5">{formatDateTimeBR(i.paid_at)}</div>}
                        </td>
                        <td className="py-2.5 text-right">
                          {i.status === "paga" ? (
                            <button onClick={() => revert(i.number)} className="fc-btn-ghost text-[11px]" data-testid={`revert-${i.number}`}>
                              <RotateCcw size={11} /> Estornar
                            </button>
                          ) : i.status === "cancelada" ? null : data.status === "ativo" ? (
                            <button onClick={() => pay(i.number)} className="fc-btn-gold text-[11px]" data-testid={`pay-${i.number}`}>
                              <DollarSign size={11} /> Pagar
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {data.kind === "acordo" && (
            <div className="fc-card p-5 mt-4">
              <h3 className="font-display text-[14px] font-semibold mb-3">Dados do acordo</h3>
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <Info label="Valor acordado" value={formatBRL(data.agreed_amount)} />
                <Info label="Capital base" value={formatBRL(data.capital)} />
                <Info label="Lucro identificado" value={formatBRL(data.lucro_total)} />
                <Info label="Contrato original" value={data.original_contract_id || "—"} />
                <Info label="Motivo" value={data.agreement_reason || "—"} className="col-span-2" />
              </div>
            </div>
          )}

          {data.observations && (
            <div className="fc-card p-5 mt-4">
              <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Observações</div>
              <div className="text-[13px] text-[#D4D4D4]">{data.observations}</div>
            </div>
          )}
        </div>

        {/* Sidebar: Comissão + Composição */}
        <div className="space-y-4">
          <div className="fc-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={14} className="text-[#D4AF37]" />
              <h3 className="font-display text-[14px] font-semibold">Comissão do indicador</h3>
            </div>
            {commission ? (
              <div data-testid="commission-card">
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Valor</div>
                <div className="font-display text-2xl font-bold text-[#D4AF37] mt-0.5">{formatBRL(commission.amount)}</div>
                <div className="text-[12px] text-[#A3A3A3] mt-1">
                  {commission.percent}% sobre {formatBRL(commission.base_amount)}
                </div>
                <div className="mt-3 pt-3 border-t border-[#1f1f1f] flex items-center justify-between">
                  <span className="text-[11px] text-[#737373] uppercase">Status</span>
                  <span className={`badge-base border ${
                    commission.status === "paga" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                    commission.status === "cancelada" ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" :
                    "bg-amber-500/15 text-amber-300 border-amber-500/40"
                  }`} data-testid="commission-status">
                    {commission.status === "paga" ? "Paga" : commission.status === "cancelada" ? "Cancelada" : "Pendente"}
                  </span>
                </div>
                {commission.paid_at && (
                  <div className="text-[10.5px] text-[#737373] mt-2">Paga em {formatDateTimeBR(commission.paid_at)}</div>
                )}
              </div>
            ) : (
              <div className="text-[12px] text-[#737373]">Sem indicador vinculado — sem comissão.</div>
            )}
          </div>

          <div className="fc-card p-5">
            <h3 className="font-display text-[14px] font-semibold mb-3">Composição da parcela</h3>
            <div className="space-y-2 text-[13px]">
              <Info label="Capital por parcela" value={formatBRL(data.installments[0]?.capital_share || 0)} />
              <Info label="Lucro por parcela" value={formatBRL(data.installments[0]?.lucro_share || 0)} />
              <div className="pt-2 mt-2 border-t border-[#1f1f1f] text-[11px] text-[#A3A3A3] leading-relaxed">
                A cada parcela paga:<br/>
                <span className="text-emerald-300">• Sócio recebe</span> capital + 50% do lucro<br/>
                <span className="text-blue-300">• Empresa recebe</span> 50% do lucro
              </div>
            </div>
          </div>

          <div className="fc-card p-5">
            <h3 className="font-display text-[14px] font-semibold mb-3">Resumo financeiro</h3>
            <div className="space-y-2 text-[13px]">
              <Info label="Total pago" value={formatBRL(data.total_paid)} />
              <Info label="Saldo restante" value={formatBRL(data.balance_remaining)} accent />
              <Info label="Criado em" value={formatDateTimeBR(data.created_at)} />
              {data.closed_at && <Info label="Encerrado em" value={formatDateTimeBR(data.closed_at)} />}
            </div>
          </div>
        </div>
      </div>

      {editModal === "interest" && (
        <EditInstallmentInterestModal
          contract={data}
          onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); load(); }}
        />
      )}
      {editModal === "due-dates" && (
        <EditInstallmentDueDatesModal
          contract={data}
          onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); load(); }}
        />
      )}
      {editModal === "values" && (
        <EditInstallmentValuesModal
          contract={data}
          onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); load(); }}
        />
      )}
      {recoveryModal && (
        <RecoveryToggleModal
          mode={recoveryModal}
          recordType="installment_contract"
          contract={data}
          onClose={() => setRecoveryModal(null)}
          onDone={() => { setRecoveryModal(null); load(); }}
        />
      )}
    </Layout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="fc-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-display text-lg sm:text-xl font-bold mt-0.5 ${accent ? "text-[#D4AF37]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function Info({ label, value, accent, className }) {
  return (
    <div className={`flex items-center justify-between ${className || ""}`}>
      <span className="text-[11px] text-[#737373] uppercase tracking-wider">{label}</span>
      <span className={`text-right ${accent ? "text-[#D4AF37] font-semibold" : "text-white"}`}>{value}</span>
    </div>
  );
}
